/*
 * The Studio (blocks/pawbau-studio.liquid): the one-screen customizer in A/B test with Mia's chat.
 *
 * The charm sits at the top and is drawn again while the name is typed; breed, piece, finish and the
 * extras sit underneath, and "Add to cart" with the running total is always at the bottom. What it
 * sells, the engine it asks and the prices all come from the chat block on the same page, so the two
 * customizers can never disagree. The cart lines are the chat's own, plus _customizer: studio.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
const DRAWN = { Silver: '#8E959B', Gold: '#C39A52', 'Rose Gold': '#CE9486', Black: '#1C1C1E' };
const FINISHES = { Silver: '#C8CBCE', Gold: '#CDA35D', 'Rose Gold': '#E0B2A4', Black: '#2E2E30' };
const CORDS = { Black: '#20201F', Ivory: '#EFE7D6', 'Army Green': '#4B5320', Burgundy: '#6E1A2B' };
const STONES = {
  Garnet: '#7B1E28', Amethyst: '#7B4EA8', 'Light Amethyst': '#C3A6DE', Aquamarine: '#7FC4D6',
  Diamond: '#E4E6EA', 'Crystal Quartz': '#ECEBEF', Emerald: '#2E8B57', Moonstone: '#EFE8DC',
  Ruby: '#C41E3A', 'Red Onyx': '#8B2B24', Peridot: '#9DC209', Sapphire: '#0F52BA',
  'Pink Tourmaline': '#F0A0B4', Opal: '#F3D9E4', Topaz: '#F0B458', Citrine: '#E3A81E',
  Tanzanite: '#5B4FC7', Turquoise: '#3FC9C0', 'Blue Zircon': '#5BB4D6',
};
const SAME = { Tabby: 'Short-Hair-Cat' };
const NAMES = { 'Short-Hair-Cat': 'Short Hair Cat/Tabby' };
const MAX_NAME = 14;

function el(tag, attrs = {}, ...kids) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === false || v == null) continue;
    if (k === 'class') node.className = v;
    else if (k === 'text') node.textContent = v;
    else if (k.startsWith('on')) node.addEventListener(k.slice(2), v);
    else node.setAttribute(k, v === true ? '' : String(v));
  }
  node.append(...kids.filter(Boolean));
  return node;
}

function svg(name, attrs, parent) {
  const node = document.createElementNS(SVG_NS, name);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  parent?.append(node);
  return node;
}

function mix(hex, to, t) {
  const a = parseInt(hex.slice(1), 16);
  const b = parseInt(to.slice(1), 16);
  const c = (shift) => Math.round(((a >> shift) & 255) * (1 - t) + ((b >> shift) & 255) * t);
  return `rgb(${c(16)} ${c(8)} ${c(0)})`;
}

/** The braided cord, the same drawing the chat uses: one knot after the other across the band. */
function braid(parent, x0, x1, cy, colour, h = 11) {
  const g = svg('g', {}, parent);
  const edge = mix(colour, '#000000', 0.5);
  const shade = mix(colour, '#000000', 0.18);
  const light = mix(colour, '#ffffff', 0.4);
  const n = Math.max(2, Math.round((x1 - x0) / (h * 0.42)));
  const step = (x1 - x0) / n;
  const top = cy - h / 2;
  svg('rect', { x: x0, y: top + 1, width: x1 - x0, height: h - 2, rx: 2, fill: edge }, g);
  for (let i = 0; i < n; i++) {
    const x = x0 + i * step;
    const up = i % 2 === 0;
    svg('path', {
      d: `M${x} ${up ? top : cy} Q${x + step / 2} ${up ? top - 2 : cy + h / 2 + 2} ${x + step} ${up ? cy : top + h}`,
      stroke: shade, 'stroke-width': step * 0.9, fill: 'none', 'stroke-linecap': 'round',
    }, g);
    svg('path', {
      d: `M${x + step * 0.2} ${up ? top + 3 : cy + 2} L${x + step * 0.7} ${up ? cy - 2 : top + h - 3}`,
      stroke: light, 'stroke-width': 1, opacity: 0.6,
    }, g);
  }
  return g;
}

function readStone(value) {
  const [month, stone = ''] = String(value).split(/\s*[-–—]\s*/);
  return { month: month.trim(), stone: stone.trim(), colour: STONES[stone.trim()] || '#CCC' };
}

class PawbauStudio extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.t = this.dataset;
    this.chat = document.querySelector('pawbau-configurator-component [ref="root"]');
    if (!this.chat) return; // the studio borrows the chat's set-up: without it there is nothing to sell
    this.cfg = this.chat.dataset;
    this.engine = (this.cfg.engine || '').replace(/\/+$/, '');
    try {
      this.catalogue = JSON.parse(this.chat.querySelector('[ref="catalogue"]')?.textContent || '{}');
    } catch {
      this.catalogue = { products: [] };
    }
    this.wares = this.catalogue.products || [];
    this.breeds = [];
    this.cats = new Set();
    this.horses = new Set();
    this.cache = new Map();
    this.state = {
      species: 'dog', breed: null, name: '', typed: false, ware: null, picked: {}, stones: [], halo: false, gift: false,
    };
    this.made = [];

    this.querySelectorAll('[data-open]').forEach((b) => b.addEventListener('click', () => this.open()));
    this.watchDock();

    if (document.documentElement.dataset.customizer !== 'studio') return;
    // links to #design on the page, and the links back from an email, open the studio
    document.addEventListener('click', (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[href$="#design"]') : null;
      if (!link || new URL(link.href, location.href).pathname !== location.pathname) return;
      event.preventDefault();
      this.open();
    });
    const q = new URLSearchParams(location.search);
    if (q.get('pet') && q.get('breed')) this.open({ breed: q.get('breed'), name: q.get('pet'), halo: q.get('halo') === '1' });
    else if (location.hash === '#design') this.open();
  }

  // — set-up ———————————————————————————————————————————————————

  async loadBreeds() {
    if (this.breedsLoaded) return this.breedsLoaded;
    this.breedsLoaded = (async () => {
      try {
        const data = await (await fetch(`${this.engine}/breeds`)).json();
        this.breeds = (data.breeds || []).filter((b) => !SAME[b]);
        this.cats = new Set(data.cats || []);
        this.horses = new Set(data.horses || []);
      } catch {
        this.breeds = [];
      }
    })();
    return this.breedsLoaded;
  }

  label(breed) {
    if (!breed) return '';
    if (NAMES[breed]) return NAMES[breed];
    return breed
      .replace(/-\(Version-[A-Z]\)$/, '')
      .replace(/-/g, ' ')
      .replace(/\b[a-z]+/g, (w) => w[0].toUpperCase() + w.slice(1));
  }

  speciesOf(breed) {
    if (this.cats.has(breed)) return 'cat';
    if (this.horses.has(breed)) return 'horse';
    return 'dog';
  }

  popular(species) {
    const raw = { dog: this.cfg.popularDogs, cat: this.cfg.popularCats, horse: this.cfg.popularHorses }[species] || '';
    return raw.split(',').map((b) => b.trim()).filter((b) => b && (!this.breeds.length || this.breeds.includes(b)));
  }

  hint(breed) {
    const line = (this.cfg.nameHints || '').split('\n').find((l) => l.split(':')[0].trim() === breed);
    return line ? line.split(':').slice(1).join(':').trim() : 'Bella';
  }

  money(cents) {
    const format = this.cfg.moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*amount[^}]*\}\}/, (Number(cents) / 100).toFixed(2));
  }

  // — opening ——————————————————————————————————————————————————

  async open(prefill = null) {
    window.pbEv?.('open');
    if (!this.dialog) this.build();
    if (!this.dialog.open) {
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
    }
    window.PawbauCampaign?.fillAll?.();
    await this.loadBreeds();

    if (!this.state.breed || prefill) {
      const q = new URLSearchParams(location.search);
      const asked = SAME[prefill?.breed || q.get('breed')] || prefill?.breed || q.get('breed');
      const breed = asked && this.breeds.includes(asked) ? asked : this.popular('dog')[0] || 'Labrador-Retriever';
      this.state.species = this.speciesOf(breed);
      this.state.breed = breed;
      if (prefill?.name) {
        this.state.name = prefill.name.slice(0, MAX_NAME);
        this.state.typed = true;
        this.nameInput.value = this.state.name;
      }
      if (prefill?.halo) this.state.halo = true;
      if (prefill) history.replaceState(null, '', location.pathname + location.hash);
    }
    if (!this.state.ware) this.chooseWare(this.wares.find((w) => w.key === this.t.piece) || this.wares[0], true);
    this.renderAll();
    this.redraw();
  }

  /** The studio's markup, made once on first open. */
  build() {
    const t = this.t;
    this.nameInput = el('input', {
      class: 'pbs__name', id: 'pbs-name', type: 'text', maxlength: MAX_NAME, autocomplete: 'off',
      autocapitalize: 'words', spellcheck: 'false', placeholder: t.namePlaceholder,
      oninput: () => this.onName(),
    });
    this.help = el('p', { class: 'pbs__help', text: t.nameHelp });
    this.stage = el('div', { class: 'pbs__stage', 'aria-live': 'polite' });
    this.stageNote = el('p', { class: 'pbs__stage-note' });
    this.speciesRow = el('div', { class: 'pbs__species', role: 'group' });
    this.chips = el('div', { class: 'pbs__chips' });
    this.search = el('input', {
      type: 'search', id: 'pbs-search', placeholder: t.searchPlaceholder, autocomplete: 'off',
      oninput: () => this.onSearch(), onfocus: () => this.onSearch(),
    });
    this.list = el('ul', { class: 'pbs__list', hidden: true });
    this.pieces = el('div', { class: 'pbs__pieces' });
    this.finishRow = el('div', { class: 'pbs__swatches' });
    this.cordField = el('div', { class: 'pbs__field' }, el('span', { class: 'pbs__label', text: t.cordLabel }));
    this.cordRow = el('div', { class: 'pbs__swatches' });
    this.cordField.append(this.cordRow);
    this.monthsBox = el('div', { class: 'pbs__months', hidden: true });
    this.stonesToggle = el('input', { type: 'checkbox', onchange: () => this.toggleMonths() });
    this.stonesCount = el('small');
    this.haloBox = el('input', { type: 'checkbox', onchange: () => { this.state.halo = this.haloBox.checked; this.redraw(); this.renderTotal(); } });
    this.haloPrice = el('small');
    this.giftBox = el('input', { type: 'checkbox', onchange: () => { this.state.gift = this.giftBox.checked; this.renderTotal(); } });
    this.giftPrice = el('small');
    this.total = el('b');
    this.addButton = el('button', { type: 'button', class: 'button pbs__add', onclick: () => this.add() });
    this.done = el('div', { class: 'pbs__done', hidden: true });

    const season = el('div', { class: 'pbs__season', 'data-pb-look': 'light' },
      el('span', { 'data-pb-camp': 'chatLeft', 'data-pb-default': '' }),
      el('span', { 'data-pb-camp': 'chatRight', 'data-pb-default': this.cfg.bundlePrice ? `Extra piece ${this.cfg.bundlePrice}` : '' }));

    this.dialog = el('dialog', { class: 'pbs', 'aria-label': t.title },
      el('header', { class: 'pbs__head' },
        el('strong', { text: t.title }),
        el('button', { type: 'button', class: 'pbs__close', 'aria-label': 'Close', onclick: () => this.dialog.close() }, this.closeIcon())),
      season,
      this.stage,
      el('div', { class: 'pbs__controls' },
        el('div', { class: 'pbs__field' }, el('label', { class: 'pbs__label', for: 'pbs-name', text: t.nameLabel }), this.nameInput, this.help),
        el('div', { class: 'pbs__field' },
          el('span', { class: 'pbs__label' }, document.createTextNode(t.breedLabel), this.breedNow = el('small')),
          this.speciesRow,
          el('div', { class: 'pbs__search' }, this.search, this.list),
          this.chips),
        el('div', { class: 'pbs__field' }, el('span', { class: 'pbs__label', text: t.pieceLabel }), this.pieces),
        el('div', { class: 'pbs__field' }, el('span', { class: 'pbs__label', text: t.finishLabel }), this.finishRow),
        this.cordField,
        el('div', { class: 'pbs__extras' },
          this.stonesLine = el('label', { class: 'pbs__extra' }, this.stonesToggle, el('span', { text: t.stonesLabel }), this.stonesCount),
          this.monthsBox,
          this.haloLine = el('label', { class: 'pbs__extra' }, this.haloBox,
            el('span', {}, el('span', { class: 'pbs__long', text: t.haloLabel }), el('span', { class: 'pbs__short', text: 'Add a memorial halo' })),
            this.haloPrice),
          this.giftLine = el('label', { class: 'pbs__extra' }, this.giftBox, el('span', { text: t.giftLabel }), this.giftPrice))),
      el('div', { class: 'pbs__foot' }, el('span', { class: 'pbs__total' }, el('small', { text: 'Total' }), this.total), this.addButton),
      this.done);
    this.dialog.addEventListener('click', (e) => {
      if (!(e.target instanceof Element) || !e.target.closest('.pbs__search')) this.list.hidden = true;
    });
    document.body.append(this.dialog);
    this.stage.append(this.stageNote);
  }

  closeIcon() {
    const s = svg('svg', { viewBox: '0 0 20 20', width: 18, height: 18, fill: 'none', stroke: 'currentColor', 'stroke-width': 1.6, 'aria-hidden': 'true' });
    svg('path', { d: 'M5 5l10 10M15 5L5 15' }, s);
    return s;
  }

  // — the controls —————————————————————————————————————————————

  renderAll() {
    this.renderSpecies();
    this.renderChips();
    this.renderPieces();
    this.renderOptions();
    this.renderExtras();
    this.renderTotal();
    this.breedNow.textContent = this.label(this.state.breed);
  }

  renderSpecies() {
    const labels = { dog: this.cfg.dogLabel || 'Dog', cat: this.cfg.catLabel || 'Cat', horse: this.cfg.horseLabel || 'Horse' };
    this.speciesRow.replaceChildren(...['dog', 'cat', 'horse'].map((sp) =>
      el('button', {
        type: 'button', 'aria-pressed': String(this.state.species === sp), text: labels[sp].replace(/^an?\s+/i, '').replace(/^./, (c) => c.toUpperCase()),
        onclick: () => { this.state.species = sp; this.renderChips(); this.renderSpecies(); },
      })));
  }

  renderChips() {
    const list = this.popular(this.state.species);
    if (this.state.breed && this.speciesOf(this.state.breed) === this.state.species && !list.includes(this.state.breed)) list.unshift(this.state.breed);
    this.chips.replaceChildren(...list.map((b) =>
      el('button', { type: 'button', class: 'pbs__chip', 'aria-pressed': String(b === this.state.breed), text: this.label(b), onclick: () => this.pickBreed(b) })));
  }

  onSearch() {
    const q = this.search.value.trim().toLowerCase();
    if (!q) { this.list.hidden = true; return; }
    const found = this.breeds.filter((b) => this.label(b).toLowerCase().includes(q)).slice(0, 8);
    this.list.replaceChildren(...(found.length
      ? found.map((b) => el('li', {}, el('button', { type: 'button', text: this.label(b), onclick: () => this.pickBreed(b) })))
      : [el('li', {}, el('span', { class: 'pbs__help', text: this.cfg.noMatch || 'No breed with that name.' }))]));
    this.list.hidden = false;
  }

  pickBreed(breed) {
    window.pbEv?.('studio_breed');
    this.state.breed = breed;
    this.state.species = this.speciesOf(breed);
    this.search.value = '';
    this.list.hidden = true;
    this.breedNow.textContent = this.label(breed);
    this.renderSpecies();
    this.renderChips();
    this.chips.querySelector('[aria-pressed="true"]')?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    this.redraw();
  }

  onName() {
    const value = this.nameInput.value.replace(/\s+/g, ' ').slice(0, MAX_NAME);
    this.state.name = value.trim();
    this.state.typed = this.state.name.length > 0;
    if (this.state.typed) window.pbEv?.('studio_typing');
    clearTimeout(this.nameTimer);
    this.nameTimer = setTimeout(() => this.redraw(), 280);
    this.renderTotal();
  }

  chooseWare(ware, quiet = false) {
    if (!ware) return;
    const before = this.state.picked;
    this.state.ware = ware;
    this.state.picked = {};
    ware.options.forEach((option, i) => {
      const values = [...new Set(ware.variants.filter((v) => v.available !== false).map((v) => v.options[i]))];
      // keep the finish across pieces when the new piece has it, otherwise Gold, otherwise the first
      this.state.picked[option] = values.includes(before[option]) ? before[option] : values.includes('Gold') ? 'Gold' : values[0];
    });
    this.state.stones = this.state.stones.filter((s) => ware.stones?.variants.some((v) => v.name === s));
    if (!quiet) {
      window.pbEv?.('studio_piece');
      this.renderPieces();
      this.renderOptions();
      this.renderExtras();
      this.renderTotal();
      this.redraw();
    }
  }

  renderPieces() {
    this.pieces.replaceChildren(...this.wares.map((w) =>
      el('button', { type: 'button', class: 'pbs__piece', 'aria-pressed': String(w === this.state.ware), onclick: () => this.chooseWare(w) },
        el('b', { text: { necklace: 'Necklace', bracelet: 'Bracelet', keychain: 'Keychain' }[w.key] || w.title }),
        el('small', { text: this.money(w.price) }))));
  }

  renderOptions() {
    const ware = this.state.ware;
    if (!ware) return;
    const row = (option) => {
      const i = ware.options.indexOf(option);
      const values = [...new Set(ware.variants.filter((v) => v.available !== false).map((v) => v.options[i]))];
      const colours = option === 'Cord' ? CORDS : FINISHES;
      return values.map((value) =>
        el('button', {
          type: 'button', class: 'pbs__swatch', 'aria-pressed': String(this.state.picked[option] === value),
          onclick: () => { this.state.picked[option] = value; this.renderOptions(); this.renderTotal(); this.redraw(false); },
        }, el('i', { style: `background:${colours[value] || '#ccc'}` }), document.createTextNode(value)));
    };
    const finishName = ware.options.find((o) => o !== 'Cord') || ware.options[0];
    this.finishRow.replaceChildren(...row(finishName));
    const hasCord = ware.options.includes('Cord');
    this.cordField.hidden = !hasCord;
    if (hasCord) this.cordRow.replaceChildren(...row('Cord'));
  }

  renderExtras() {
    const ware = this.state.ware;
    const stones = ware?.stones;
    const max = Number(this.cfg.stonesMax) || 4;
    this.stonesLine.hidden = !stones;
    if (stones) {
      const picked = this.state.stones.length;
      this.stonesCount.textContent = picked ? `${picked} · +${this.money(picked * Number(stones.price))}` : `+${this.money(stones.price)} each`;
      this.stonesToggle.checked = !this.monthsBox.hidden || picked > 0;
      const note = el('p', { class: 'pbs__months-note', text: `${this.money(stones.price)} each · up to ${max}` });
      this.monthsBox.replaceChildren(note, ...stones.variants.map((v) => {
        const s = readStone(v.name);
        return el('button', {
          type: 'button', class: 'pbs__month', 'aria-pressed': String(this.state.stones.includes(v.name)),
          onclick: () => {
            const at = this.state.stones.indexOf(v.name);
            if (at >= 0) this.state.stones.splice(at, 1);
            else if (this.state.stones.length < max) this.state.stones.push(v.name);
            this.renderExtras();
            this.renderTotal();
            this.redraw(false);
          },
        }, el('i', { style: `background:${s.colour}` }), document.createTextNode(s.month.slice(0, 3)));
      }));
    } else {
      this.monthsBox.hidden = true;
    }
    const { halo, gift } = this.catalogue;
    this.haloLine.hidden = !halo;
    if (halo) { this.haloPrice.textContent = `+${this.money(halo.price)}`; this.haloBox.checked = this.state.halo; }
    this.giftLine.hidden = !gift;
    if (gift) { this.giftPrice.textContent = `+${this.money(gift.price)}`; this.giftBox.checked = this.state.gift; }
  }

  toggleMonths() {
    this.monthsBox.hidden = !this.stonesToggle.checked;
    if (!this.stonesToggle.checked) {
      this.state.stones = [];
      this.renderExtras();
      this.renderTotal();
      this.redraw(false);
    }
  }

  variant() {
    const ware = this.state.ware;
    return ware?.variants.find((v) => ware.options.every((o, i) => v.options[i] === this.state.picked[o])) || null;
  }

  totalCents() {
    const v = this.variant();
    let cents = Number(v?.price ?? this.state.ware?.price ?? 0);
    const stones = this.state.ware?.stones;
    if (stones) cents += this.state.stones.length * Number(stones.price);
    if (this.state.halo && this.catalogue.halo) cents += Number(this.catalogue.halo.price);
    if (this.state.gift && this.catalogue.gift) cents += Number(this.catalogue.gift.price);
    return cents;
  }

  renderTotal() {
    this.total.textContent = this.money(this.totalCents());
    const ready = this.state.typed && this.state.breed && this.variant() && this.fits !== false;
    this.addButton.disabled = !ready || this.adding;
    this.addButton.textContent = this.adding ? this.t.addingLabel : this.t.addLabel;
  }

  // — the drawing ——————————————————————————————————————————————

  async charm(breed, name, halo) {
    const key = `${breed}|${name}|${halo ? 1 : 0}`;
    if (this.cache.has(key)) return this.cache.get(key);
    const url = `${this.engine}/preview?breed=${encodeURIComponent(breed)}&name=${encodeURIComponent(name)}${halo ? '&halo=1' : ''}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(String(res.status));
    const data = await res.json();
    this.cache.set(key, data);
    return data;
  }

  /** Draws the piece again. `fetchCharm` false: only the finish, cord or stones changed. */
  async redraw(fetchCharm = true) {
    const { breed, typed } = this.state;
    if (!breed || !this.state.ware) return;
    const name = typed ? this.state.name : this.hint(breed);
    this.stageNote.textContent = typed ? '' : this.t.typeName;
    if (fetchCharm || !this.drawn) {
      const ticket = (this.ticket = (this.ticket || 0) + 1);
      this.stage.classList.add('is-drawing');
      try {
        const data = await this.charm(breed, name, this.state.halo);
        if (ticket !== this.ticket) return;
        this.drawn = data;
        this.fits = typed ? data.fits !== false : true;
        this.help.textContent = this.fits ? this.t.nameHelp : this.t.tooLong;
        this.help.classList.toggle('is-warn', !this.fits);
        if (typed) {
          this.trackPreview(name, breed);
          if (this.fits) window.pbEv?.('named');
          else window.pbEv?.('studio_too_long');
        }
      } catch {
        if (ticket !== this.ticket) return;
        this.help.textContent = this.t.drawingFailed;
        this.help.classList.add('is-warn');
      } finally {
        if (ticket === this.ticket) this.stage.classList.remove('is-drawing');
      }
    }
    this.paint();
    this.renderTotal();
    this.prepareEarly();
  }

  /** The piece as worn: the charm on its chain, cord or ring, in the chosen finish, with its stones. */
  paint() {
    const data = this.drawn;
    if (!data) return;
    const kind = this.state.ware.key;
    const finish = this.state.picked.Finish || this.state.picked[this.state.ware.options[0]];
    const colour = DRAWN[finish] || '#C39A52';
    const art = svg('svg', { viewBox: '0 0 200 140', role: 'img', 'aria-label': `${this.label(this.state.breed)} charm` });
    const [, , vw, vh] = String(data.viewBox).split(/\s+/).map(Number);
    const ratio = vw && vh ? vh / vw : 0.75;
    let w = 112;
    let h = w * ratio;
    if (h > 92) { h = 92; w = h / ratio; }
    const x = 100 - w / 2;
    let y;
    const chain = { stroke: mix(colour.startsWith('#') ? colour : '#C39A52', '#ffffff', 0.15), fill: 'none' };
    const stoneDots = this.state.stones.map((s) => readStone(s).colour);

    if (kind === 'bracelet') {
      y = 70 - h / 2;
      const cord = CORDS[this.state.picked.Cord] || '#20201F';
      braid(art, 4, x + 6, 70, cord);
      braid(art, x + w - 6, 196, 70, cord);
      stoneDots.forEach((c, i) => svg('circle', { cx: x - 8 - i * 9, cy: 70, r: 3.6, fill: c, stroke: 'rgb(0 0 0 / .2)', 'stroke-width': 0.6 }, art));
    } else if (kind === 'keychain') {
      svg('circle', { cx: 100, cy: 15, r: 11, ...chain, 'stroke-width': 2.4 }, art);
      svg('line', { x1: 100, y1: 26, x2: 100, y2: 40, ...chain, 'stroke-width': 1.6, 'stroke-dasharray': '3 2' }, art);
      y = 42;
      stoneDots.forEach((c, i) => svg('circle', { cx: 100 + (i % 2 ? 6 : -6), cy: 30 + i * 3, r: 3.4, fill: c, stroke: 'rgb(0 0 0 / .2)', 'stroke-width': 0.6 }, art));
    } else {
      svg('path', { d: 'M8 0 Q100 74 192 0', ...chain, 'stroke-width': 1.3 }, art);
      y = 40;
      stoneDots.forEach((c, i) => svg('circle', { cx: 100 + (i - (stoneDots.length - 1) / 2) * 8, cy: 36, r: 3.6, fill: c, stroke: 'rgb(0 0 0 / .2)', 'stroke-width': 0.6 }, art));
    }
    const holder = svg('svg', { x, y, width: w, height: h, viewBox: data.viewBox, preserveAspectRatio: 'xMidYMid meet' }, art);
    svg('path', { d: data.path, 'fill-rule': 'evenodd', fill: colour }, holder);
    if (!this.state.typed) holder.setAttribute('opacity', '0.55');
    const old = this.stage.querySelector('svg');
    if (old) old.replaceWith(art);
    else this.stage.prepend(art);
  }

  // — the cart —————————————————————————————————————————————————

  fileKey() {
    const metal = DRAWN[this.state.picked.Finish] || '#1C1C1C';
    const kind = this.state.ware?.key || 'necklace';
    return { metal, kind, key: [this.state.breed, this.state.name, metal, kind, this.state.halo ? 1 : 0].join('|') };
  }

  prepare(metal, kind) {
    const { breed, name, halo } = this.state;
    const url = `${this.engine}/prepare?breed=${encodeURIComponent(breed)}&name=${encodeURIComponent(name)}&colour=${encodeURIComponent(metal.replace('#', ''))}&piece=${encodeURIComponent(kind)}${halo ? '&halo=1' : ''}`;
    return fetch(url)
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => ({ url: d.url || '', image: d.image || '' }))
      .catch(() => ({ url: '', image: '' }));
  }

  /** The cutting file starts as soon as name, breed, piece and finish are settled, before the click. */
  prepareEarly() {
    if (!this.state.typed || this.fits === false) return;
    clearTimeout(this.earlyTimer);
    this.earlyTimer = setTimeout(() => {
      const { metal, kind, key } = this.fileKey();
      if (this.early?.key === key) return;
      this.early = { key, files: this.prepare(metal, kind) };
    }, 1200);
  }

  async add() {
    const variant = this.variant();
    if (!variant || !this.state.typed || this.adding) return;
    window.pbEv?.('add_click');
    if (this.state.stones.length) window.pbEv?.('studio_stones');
    if (this.state.gift) window.pbEv?.('studio_gift');
    this.adding = true;
    this.renderTotal();
    const { metal, kind, key } = this.fileKey();
    // one request per file: the early one if it is for this piece, otherwise this one, never both at once
    clearTimeout(this.earlyTimer);
    if (this.early?.key !== key) this.early = { key, files: this.prepare(metal, kind) };
    let files = await this.early.files;
    if (!files.url) {
      // the shop can take a moment to hand back a brand-new file: ask once more
      await new Promise((r) => setTimeout(r, 1500));
      this.early = { key, files: this.prepare(metal, kind) };
      files = await this.early.files;
    }
    const { breed, name, halo, gift } = this.state;
    const ware = this.state.ware;
    const stamp = String(Date.now());
    const parent = ware.handle || ware.key;
    const stoneNames = {};
    this.state.stones.forEach((v, i) => { stoneNames[`Birthstone ${i + 1}`] = v; });
    const items = [{
      id: variant.id,
      quantity: 1,
      properties: {
        Breed: this.label(breed),
        Name: name,
        ...stoneNames,
        ...(gift ? { 'Gift box': this.cfg.giftYes || 'Yes' } : {}),
        _breed: breed,
        _piece: kind,
        ...(halo ? { _halo: '1' } : {}),
        _created_at: stamp,
        ...(files.url ? { _dxf: files.url } : {}),
        ...(files.image ? { _preview: files.image } : {}),
        _customizer: 'studio',
      },
    }];
    for (const value of this.state.stones) {
      const stone = ware.stones?.variants.find((v) => v.name === value);
      if (stone) items.push({ id: stone.id, quantity: 1, properties: { _parent: parent, _created_at: stamp } });
    }
    if (halo && this.catalogue.halo?.id) items.push({ id: this.catalogue.halo.id, quantity: 1, properties: { _parent: parent, _created_at: stamp } });
    if (gift && this.catalogue.gift?.id) items.push({ id: this.catalogue.gift.id, quantity: 1, properties: { _parent: parent, _created_at: stamp } });

    try {
      const res = await fetch(this.cfg.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error(String(res.status));
      // the order says which customizer made it: that is how the test is read
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { Customizer: 'studio' } }),
      }).catch(() => {});
      window.pbEv?.('added');
      this.made.push({ name, breed, kind });
      this.trackCart(variant, files.image);
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      this.showDone(name, kind);
    } catch {
      this.help.textContent = this.t.cartFailed;
      this.help.classList.add('is-warn');
    } finally {
      this.adding = false;
      this.renderTotal();
    }
  }

  showDone(name, kind) {
    const pieceWord = { necklace: 'necklace', bracelet: 'bracelet', keychain: 'keychain' };
    const camp = window.PawbauCampaign;
    const c = camp?.active?.();
    const offer = (this.t.doneOffer || '').replace(/\[extra_price\]/g, this.cfg.bundlePrice || '');
    const others = this.wares.filter((w) => w.key !== kind);
    const sheet = el('div', { class: 'pbs__sheet' },
      el('h3', { text: (this.t.doneTitle || '').replace('[name]', name).replace('[piece]', pieceWord[kind] || 'piece') }),
      el('p', { text: offer }));
    if (c?.dealNote && camp) {
      const note = el('p');
      note.innerHTML = camp.text(c.dealNote, c); // escaped editor text with its placeholders
      sheet.append(note);
    }
    sheet.append(
      el('div', { class: 'pbs__again' }, ...others.map((w) =>
        el('button', { type: 'button', text: `${name} on a ${pieceWord[w.key] || w.title}`, onclick: () => this.again(w) }))),
      el('button', { type: 'button', class: 'pbs__other', text: this.t.doneOther, onclick: () => this.again(null) }),
      el('button', { type: 'button', class: 'button', text: this.t.doneCart, onclick: () => { location.href = this.cfg.cartUrl || '/cart'; } }));
    this.done.replaceChildren(sheet);
    this.done.hidden = false;
  }

  /** Another piece: the same pet on a new piece, or a new pet on the same piece. */
  again(ware) {
    window.pbEv?.(ware ? 'studio_again_piece' : 'studio_again_pet');
    this.done.hidden = true;
    this.state.stones = [];
    this.state.gift = false;
    this.monthsBox.hidden = true;
    if (ware) {
      this.chooseWare(ware);
    } else {
      this.state.name = '';
      this.state.typed = false;
      this.state.halo = false;
      this.nameInput.value = '';
      this.renderExtras();
      this.renderTotal();
      this.redraw();
      this.nameInput.focus();
    }
    this.dialog.querySelector('.pbs__controls').scrollTop = 0;
  }

  // — the sticky button on the page, and Klaviyo ———————————————————

  watchDock() {
    const dock = this.querySelector('[data-dock]');
    const first = this.querySelector('[data-open]');
    if (!dock || !first || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(([entry]) => {
      dock.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
    }).observe(first);
  }

  track(metric, props) {
    try {
      (window.klaviyo = window.klaviyo || []).push(['track', metric, { ...props, Customizer: 'studio' }]);
    } catch {}
  }

  trackPreview(name, breed) {
    const key = `${breed}|${name}`;
    this.tracked = this.tracked || new Set();
    if (this.tracked.has(key)) return;
    clearTimeout(this.trackTimer);
    // only once they stop typing: every letter is not a new pet
    this.trackTimer = setTimeout(() => {
      this.tracked.add(key);
      const resume = new URL(location.pathname, location.origin);
      resume.searchParams.set('pet', name);
      resume.searchParams.set('breed', breed);
      resume.hash = 'design';
      this.track('Preview Created', {
        PetName: name, Breed: this.label(breed), BreedKey: breed, Species: this.speciesOf(breed), Halo: this.state.halo,
        ResumeURL: resume.href, PageURL: location.origin + location.pathname,
      });
    }, 2500);
  }

  trackCart(variant, image) {
    const ware = this.state.ware;
    this.track('Added Piece to Cart', {
      PetName: this.state.name, Breed: this.label(this.state.breed), BreedKey: this.state.breed, Piece: ware.key,
      ProductName: ware.title, Finish: this.state.picked.Finish || '', Birthstones: this.state.stones, GiftBox: this.state.gift,
      Halo: this.state.halo, ImageURL: image ? new URL(image, location.href).href : '',
      CartURL: new URL(this.cfg.cartUrl || '/cart', location.origin).href, PiecesInCart: this.made.length,
      $value: Number(variant.price) / 100,
    });
  }
}

if (!customElements.get('pawbau-studio')) customElements.define('pawbau-studio', PawbauStudio);
